/**
 * Pure decision module for the post-signup / post-sign-in push flow.
 *
 * When a user transitions from signed-out to signed-in on the native
 * app, we combine three signals to decide what to do:
 *   - the pre-signup localStorage choice (they may have opted in on
 *     first launch, before creating an account)
 *   - the current OS-level permission state (they may have granted
 *     via our welcome prompt, granted via a totally different app's
 *     prompt, or toggled it in Settings)
 *   - whether the server already has a PushToken row for this user +
 *     venue (repeat sign-in on a device that already registered)
 *
 * Returns one of:
 *   - `register_silently`: OS says granted; call PushNotifications
 *     .register() to get the token, POST to /api/me/push, and record
 *     PushPromptState { response: "granted" }. No UI.
 *   - `record_denial_no_prompt`: user said no somewhere (OS or our
 *     welcome prompt); record PushPromptState { response:
 *     "system_denied" } so the signed-in modal never fires.
 *   - `wait_for_prompt_modal`: nothing to auto-do; let the existing
 *     signed-in PushPromptModal handle it if the modal's own state
 *     says to prompt.
 *   - `no_action`: already have a token or user is not on native.
 *
 * Pure module: no React, no fetch, no Capacitor. Trivially testable.
 */

export type OsPermissionState = "granted" | "denied" | "prompt";

export type PreAuthResponse = "granted" | "denied" | "asked_dismissed";

export type PostAuthDecisionInput = {
  isNativeApp: boolean;
  /** Whether the server has a PushToken row for (userId, venueId). */
  serverHasToken: boolean;
  /** Current OS-level permission (via Capacitor's checkPermissions). */
  osPermission: OsPermissionState;
  /** The pre-signup localStorage choice, if any. */
  preAuthResponse: PreAuthResponse | null;
};

export type PostAuthAction =
  | "register_silently"
  | "record_denial_no_prompt"
  | "wait_for_prompt_modal"
  | "no_action";

export type PostAuthDecision = {
  action: PostAuthAction;
  reason: string;
};

/**
 * Decide what the post-auth push hook should do RIGHT NOW.
 *
 * Priority order (most specific first):
 *   1. Not native or already has a token: no_action.
 *   2. OS says denied: record_denial_no_prompt (user is out; don't
 *      nag with a modal that can't succeed).
 *   3. OS says granted: register_silently (regardless of preAuth,
 *      the OS is the source of truth for "can we push?").
 *   4. OS says prompt + preAuth=granted: this is the "user clicked
 *      Turn On in our welcome prompt but the OS state didn't actually
 *      flip to granted" corner: try register_silently anyway (the
 *      register call will re-check permission; if it fails it'll
 *      throw and the caller records the denial).
 *   5. OS says prompt + preAuth=denied: record_denial_no_prompt.
 *   6. OS says prompt + preAuth=asked_dismissed: wait_for_prompt_modal
 *      (they postponed pre-signup; give them another chance via the
 *      signed-in modal).
 *   7. OS says prompt + preAuth=null: wait_for_prompt_modal (they
 *      never saw our welcome prompt, hit signup directly; existing
 *      signed-in modal will handle).
 */
/**
 * Client-side helper: given the full list of PushToken rows for the
 * signed-in user (any venue), does one belong to THIS venue? Extracted
 * as a pure export so root-vitest can pin it without importing a
 * .tsx module. Peer-review 2026-09-28 HIGH.
 */
export function hasVenueTokenIn(
  tokens: Array<{ venueId: string | null }>,
  venueId: string,
): boolean {
  return tokens.some((t) => t.venueId === venueId);
}

export function decidePostAuthPushAction(
  input: PostAuthDecisionInput,
): PostAuthDecision {
  if (!input.isNativeApp) {
    return { action: "no_action", reason: "not_native" };
  }
  if (input.serverHasToken) {
    return { action: "no_action", reason: "server_already_has_token" };
  }
  if (input.osPermission === "denied") {
    return {
      action: "record_denial_no_prompt",
      reason: "os_denied_at_signin_time",
    };
  }
  if (input.osPermission === "granted") {
    return { action: "register_silently", reason: "os_granted_ready_to_register" };
  }

  // osPermission === "prompt" (not yet determined at OS level).
  switch (input.preAuthResponse) {
    case "granted":
      // Optimistic: our welcome prompt said granted but OS says
      // prompt. This can happen if the plugin returned "granted" from
      // requestPermissions but a subsequent checkPermissions returns
      // "prompt" due to iOS state weirdness. Try registering anyway;
      // the caller triages any thrown error.
      return {
        action: "register_silently",
        reason: "preauth_granted_optimistic_register",
      };
    case "denied":
      return {
        action: "record_denial_no_prompt",
        reason: "preauth_denied",
      };
    case "asked_dismissed":
    case null:
    default:
      return {
        action: "wait_for_prompt_modal",
        reason: "defer_to_signed_in_modal",
      };
  }
}
